import { PlusCircle, Sparkles, FileText, UploadCloud, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ActionCard from "@/components/ui/action-card";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { UploadPolicyModal } from "@/components/policy/UploadPolicyModal";
import { motion, AnimatePresence } from "framer-motion";

const Begin = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);

  const actions = [
    {
      title: "Create a New Policy",
      description: "Start from scratch with our comprehensive policy builder. Perfect for creating custom policies tailored to your organization's specific needs.",
      icon: PlusCircle,
      href: "/policy-creator",
      bgClass: "bg-blue-100 dark:bg-blue-900/30",
      hoverBgClass: "hover:bg-blue-200 dark:hover:bg-blue-900/50",
      iconColorClass: "text-blue-700 dark:text-blue-300",
      textClass: "text-foreground",
      highlight: true
    },
    {
      title: "Upload Policy Document",
      description: "Upload existing policy documents in various formats. We'll help you convert and organize them in our system.",
      icon: UploadCloud,
      action: () => setShowUploadModal(true),
      bgClass: "bg-green-100 dark:bg-green-900/30",
      hoverBgClass: "hover:bg-green-200 dark:hover:bg-green-900/50",
      iconColorClass: "text-green-700 dark:text-green-300",
      textClass: "text-foreground"
    },
    {
      title: "Start with AI Assistant",
      description: "Let our AI assistant help you create policies faster with intelligent suggestions and automated content generation.",
      icon: Sparkles,
      href: "/ai-agent",
      bgClass: "bg-purple-100 dark:bg-purple-900/30",
      hoverBgClass: "hover:bg-purple-200 dark:hover:bg-purple-900/50",
      iconColorClass: "text-purple-700 dark:text-purple-300",
      textClass: "text-foreground"
    },
    {
      title: "Start with a Template",
      description: "Choose from our library of pre-built policy templates covering common scenarios and industry standards.",
      icon: FileText,
      href: "/templates",
      bgClass: "bg-orange-100 dark:bg-orange-900/30",
      hoverBgClass: "hover:bg-orange-200 dark:hover:bg-orange-900/50",
      iconColorClass: "text-orange-700 dark:text-orange-300",
      textClass: "text-foreground"
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
          <h1 className="text-3xl font-medium text-foreground">Create a Policy</h1>
          <p className="text-muted-foreground">
            Choose how you'd like to get started with creating your new policy
          </p>
        </div>
      </div>

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl">
        <AnimatePresence>
          {actions.map((action, index) => {
            const Icon = action.icon;
            
            if (action.action) {
              return (
                <motion.div
                  key={action.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 30 }}
                  transition={{ delay: 0.2 * index, duration: 0.5 }}
                  className="cursor-pointer"
                >
                  <ActionCard
                    title={action.title}
                    description={action.description}
                    icon={Icon}
                    onClick={action.action}
                    bgClass={action.bgClass}
                    hoverBgClass={action.hoverBgClass}
                    iconColorClass={action.iconColorClass}
                    textClass={action.textClass}
                    className={action.highlight ? 'ring-2 ring-blue-200 dark:ring-blue-800 h-48' : 'h-48'}
                  />
                </motion.div>
              );
            }
            
            return (
              <motion.div
                key={action.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 30 }}
                transition={{ delay: 0.2 * index, duration: 0.5 }}
              >
                <ActionCard
                  title={action.title}
                  description={action.description}
                  icon={Icon}
                  href={action.href!}
                  bgClass={action.bgClass}
                  hoverBgClass={action.hoverBgClass}
                  iconColorClass={action.iconColorClass}
                  textClass={action.textClass}
                  className={action.highlight ? 'ring-2 ring-blue-200 dark:ring-blue-800 h-48' : 'h-48'}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Additional Information */}
      <div className="max-w-6xl">
        <Card className="bg-muted/30 border-dashed">
          <CardHeader>
            <CardTitle className="text-lg">Need Help Choosing?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <h4 className="font-medium text-foreground mb-2">🆕 New to Policy Creation?</h4>
                <p className="text-muted-foreground">
                  Start with a <strong>template</strong> or use our <strong>AI assistant</strong> for guided creation with suggestions and best practices.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-2">📄 Have Existing Policies?</h4>
                <p className="text-muted-foreground">
                  <strong>Upload</strong> your current policy documents to convert them into our system for better organization and management.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-2">🎯 Need Full Control?</h4>
                <p className="text-muted-foreground">
                  <strong>Create from scratch</strong> using our comprehensive policy builder with rich text editing and workflow management.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-2">⚡ Want Speed?</h4>
                <p className="text-muted-foreground">
                  Use our <strong>AI assistant</strong> to generate policy content quickly based on your requirements and industry standards.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <UploadPolicyModal 
        open={showUploadModal} 
        onOpenChange={setShowUploadModal} 
      />
    </div>
  );
};

export default Begin;
